import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Zap, Calendar, CreditCard, CloudRain, Sparkles, Clock, User, Map, Upload } from 'lucide-react';

interface Article {
  title: string;
  category: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const ARTICLES: Record<string, Article> = {
  'comprendre-statuts': {
    title: 'Comprendre les statuts de candidature',
    category: 'Exposants',
    icon: <Clock className="text-yellow-400" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">Après avoir postulé à un festival, votre candidature passe par plusieurs statuts. Voici ce que chacun signifie et ce que vous devez faire.</p>

        <div className="space-y-4">
          <div className="flex items-start gap-4 bg-yellow-400/5 border border-yellow-400/20 rounded-2xl p-5">
            <Clock className="w-5 h-5 text-yellow-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-bold text-white text-base mb-1">En attente</h3>
              <p className="text-sm">Votre candidature est bien reçue. L'organisateur n'a pas encore pris de décision. Vous serez notifié dès qu'il répond.</p>
              <p className="text-sm text-yellow-400/80 mt-2 font-medium">Action requise : aucune. Patientez.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 bg-[#FF5C00]/5 border border-[#FF5C00]/20 rounded-2xl p-5">
            <Zap className="w-5 h-5 text-[#FF5C00] mt-0.5 shrink-0" />
            <div>
              <h3 className="font-bold text-white text-base mb-1">Paiement requis</h3>
              <p className="text-sm">Félicitations ! L'organisateur vous a retenu. Il reste une étape : régler les frais de gestion Festin pour confirmer définitivement votre emplacement.</p>
              <p className="text-sm text-[#FF5C00] mt-2 font-medium">Action requise : cliquez sur "Procéder au paiement" dans la carte de candidature.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 bg-emerald-400/5 border border-emerald-400/20 rounded-2xl p-5">
            <Sparkles className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-bold text-white text-base mb-1">Confirmé</h3>
              <p className="text-sm">Paiement validé, votre place est officiellement réservée. Un contrat PDF a été généré automatiquement — vous pouvez le télécharger depuis la fiche de candidature.</p>
              <p className="text-sm text-emerald-400 mt-2 font-medium">Action requise : téléchargez votre contrat et notez les infos logistiques.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 bg-red-400/5 border border-red-400/20 rounded-2xl p-5">
            <CloudRain className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-bold text-white text-base mb-1">Refusé</h3>
              <p className="text-sm">L'organisateur n'a pas retenu votre candidature pour cette édition. Votre profil reste visible et vous pouvez postuler à d'autres festivals.</p>
              <p className="text-sm text-gray-500 mt-2">Aucun frais n'est dû en cas de refus.</p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
          <h3 className="font-bold text-white mb-2">Où voir mes candidatures ?</h3>
          <p className="text-sm">Allez dans <strong className="text-white">Mes Festivals</strong> (menu en bas ou en haut). Trois onglets : <em>En attente</em>, <em>Acceptées</em>, <em>Historique</em>.</p>
        </div>
      </div>
    ),
  },

  'profil-exposant-complet': {
    title: 'Compléter son profil exposant à 100%',
    category: 'Exposants',
    icon: <User className="text-[#FF5C00]" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">Un profil complet améliore votre score de visibilité et rassure les organisateurs. Voici tout ce que vous pouvez renseigner.</p>

        <h2 className="text-xl font-bold text-white">Identité (obligatoire)</h2>
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li><strong className="text-white">Nom commercial</strong> — Le nom de votre stand tel qu'il apparaît sur le marché</li>
          <li><strong className="text-white">Type de stand</strong> — Food Truck, Artisanat/Créateurs, Mode/Friperie, Vinyles, Tatouage/Piercing, Engagé/Associatif, Partenaires/Sponsors, Services/Bien-être ou Autre</li>
          <li><strong className="text-white">Région principale</strong> — Pour matcher avec les festivals proches de vous</li>
          <li><strong className="text-white">Description</strong> — Présentez votre univers en quelques phrases</li>
        </ul>

        <h2 className="text-xl font-bold text-white">Visuels</h2>
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li><strong className="text-white">Logo / Photo de profil</strong> — Visible dans les résultats de recherche organisateurs</li>
          <li><strong className="text-white">Photo de couverture (bannière)</strong> — S'affiche en haut de votre fiche détaillée</li>
          <li><strong className="text-white">Galerie photos</strong> — Montrez votre stand, vos produits, votre équipe</li>
        </ul>

        <h2 className="text-xl font-bold text-white">Logistique (important pour les organisateurs)</h2>
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li><strong className="text-white">Dimensions du stand</strong> — Largeur et profondeur en mètres</li>
          <li><strong className="text-white">Besoin en électricité</strong> — Oui/Non + puissance approximative en kW</li>
          <li><strong className="text-white">Accès eau potable</strong> — Pour food trucks et services</li>
          <li><strong className="text-white">Logistique véhicule</strong> — Si vous venez avec un camion/van, précisez l'immatriculation et le modèle</li>
        </ul>

        <h2 className="text-xl font-bold text-white">Documents (renforce votre crédibilité)</h2>
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li><strong className="text-white">Assurance RC Pro</strong> — Souvent exigée par les organisateurs</li>
          <li><strong className="text-white">Kbis / SIRET</strong> — Prouve votre existence légale</li>
          <li><strong className="text-white">Agréments Sanitaires</strong> — Indispensable pour food trucks et restauration</li>
          <li><strong className="text-white">Press Kit / Menu PDF</strong> — Donnez envie aux organisateurs de vous sélectionner</li>
        </ul>

        <div className="bg-[#FF5C00]/10 border border-[#FF5C00]/20 rounded-2xl p-5">
          <p className="text-sm"><strong className="text-[#FF5C00]">Conseil :</strong> Un profil avec photos, dimensions, et RC Pro uploadée obtient en moyenne 3× plus de réponses positives des organisateurs.</p>
        </div>
      </div>
    ),
  },

  'publier-festival': {
    title: 'Créer et publier son festival en 5 étapes',
    category: 'Organisateurs',
    icon: <Calendar className="text-[#00A3FF]" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">La création d'un festival se fait en 5 étapes guidées. Vous pouvez sauvegarder à tout moment et revenir plus tard.</p>

        {[
          { n: 1, title: 'Infos de base', desc: 'Nom du festival, organisateur, catégorie (musique, culturel, gastronomique, foire, marché, sport, local…), dates, créneaux horaires (journée, nuit, full pass, plage libre). Vous pouvez importer automatiquement depuis une URL Shotgun.' },
          { n: 2, title: 'Localisation', desc: 'Adresse du site sur la carte interactive (OpenStreetMap). Épinglez l\'entrée principale, les zones de stationnement exposants, etc.' },
          { n: 3, title: 'Modèle économique', desc: 'Définissez les types de stands que vous acceptez, leurs dimensions, prix d\'emplacement par jour, et notes internes. C\'est ici que vous configurez si les frais Festin seront un forfait ou un pourcentage.' },
          { n: 4, title: 'Détails & branding', desc: 'Description complète, logo, photo de couverture, lien billetterie (Shotgun, Dice…), numéro d\'édition, affluence passée et lien vers votre site.' },
          { n: 5, title: 'Publication', desc: 'Vérifiez le récapitulatif et publiez. Le statut passe de "Brouillon" (orange) à "Publié" (vert). Votre festival est immédiatement visible pour les exposants.' },
        ].map(step => (
          <div key={step.n} className="flex gap-4">
            <div className="w-8 h-8 bg-[#00A3FF]/15 text-[#00A3FF] rounded-full flex items-center justify-center font-black text-sm shrink-0 mt-0.5">{step.n}</div>
            <div>
              <h3 className="font-bold text-white">{step.title}</h3>
              <p className="text-sm mt-1">{step.desc}</p>
            </div>
          </div>
        ))}

        <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
          <h3 className="font-bold text-white mb-2">Le festival doit démarrer dans au moins 15 jours</h3>
          <p className="text-sm">Une validation empêche de publier un festival à moins de 15 jours du début, pour laisser le temps aux exposants de préparer leur venue.</p>
        </div>
      </div>
    ),
  },

  'plan-de-masse': {
    title: 'Utiliser le plan de masse',
    category: 'Organisateurs',
    icon: <Map className="text-[#00A3FF]" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">Le plan de masse est l'outil cartographique intégré à votre tableau de bord festival. Il vous permet de visualiser et d'organiser tous vos emplacements.</p>

        <h2 className="text-xl font-bold text-white">Les outils disponibles</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex items-start gap-2"><span className="text-[#00A3FF] font-bold shrink-0">Sélection</span> — Cliquez sur un élément pour le déplacer ou le configurer</li>
          <li className="flex items-start gap-2"><span className="text-[#00A3FF] font-bold shrink-0">Zone</span> — Tracez des polygones pour délimiter des espaces (scène, food court, allée)</li>
          <li className="flex items-start gap-2"><span className="text-[#00A3FF] font-bold shrink-0">Électricité</span> — Placez des points d'alimentation électrique sur la carte</li>
          <li className="flex items-start gap-2"><span className="text-[#00A3FF] font-bold shrink-0">Eau</span> — Placez des points d'accès eau potable</li>
        </ul>

        <h2 className="text-xl font-bold text-white">Placer les exposants acceptés</h2>
        <p className="text-sm">Les exposants dont la candidature est "Confirmée" apparaissent dans la liste latérale. Faites-les glisser sur le plan pour leur attribuer un emplacement précis.</p>

        <h2 className="text-xl font-bold text-white">Optimisation IA</h2>
        <p className="text-sm">Le bouton "Optimiser le plan" utilise l'IA pour répartir automatiquement les stands en tenant compte de leurs catégories et besoins techniques.</p>

        <h2 className="text-xl font-bold text-white">Export CSV</h2>
        <p className="text-sm">Cliquez sur "Exporter la logistique (CSV)" pour télécharger un tableur avec tous les stands placés : nom, emplacement, dimensions, besoins électricité/eau, contact.</p>
      </div>
    ),
  },

  'import-shotgun': {
    title: 'Importer un festival depuis Shotgun',
    category: 'Organisateurs',
    icon: <Upload className="text-[#00A3FF]" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">Si votre festival est déjà sur Shotgun, vous pouvez importer automatiquement ses informations en quelques secondes.</p>

        <h2 className="text-xl font-bold text-white">Comment ça marche</h2>
        <ol className="list-decimal pl-5 space-y-3 text-sm">
          <li>Dans la <strong className="text-white">première étape</strong> de création de festival, copiez l'URL de votre événement Shotgun (ex: <code className="text-[#FF5C00] bg-white/5 px-1 rounded">https://shotgun.live/fr/events/mon-festival</code>)</li>
          <li>Collez-la dans le champ "URL de l'événement" et cliquez sur "Importer"</li>
          <li>L'IA analyse la page et extrait automatiquement : <strong className="text-white">nom, dates, lieu, description</strong></li>
          <li>Vérifiez les informations pré-remplies et complétez ce qui manque</li>
        </ol>

        <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
          <h3 className="font-bold text-white mb-2">Quelles plateformes sont supportées ?</h3>
          <p className="text-sm">L'import fonctionne principalement avec Shotgun. D'autres plateformes (Dice, Weezevent) peuvent fonctionner si leur page est publique et structurée. En cas d'échec, complétez manuellement.</p>
        </div>

        <div className="bg-[#FF5C00]/10 border border-[#FF5C00]/20 rounded-2xl p-5">
          <p className="text-sm"><strong className="text-[#FF5C00]">Important :</strong> L'import ne publie pas votre festival automatiquement. Il pré-remplit le formulaire — vous restez maître de ce qui est enregistré.</p>
        </div>
      </div>
    ),
  },

  'tarification-festin': {
    title: 'Tarification et frais Festin',
    category: 'Paiement',
    icon: <CreditCard className="text-purple-400" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">Festin se rémunère exclusivement auprès des exposants. Les organisateurs ne paient rien.</p>

        <h2 className="text-xl font-bold text-white">Pour les exposants</h2>
        <div className="space-y-3">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="font-bold text-white mb-1">Stand gratuit (organisateur ne facture pas d'emplacement)</div>
            <p className="text-sm">Forfait fixe <strong className="text-[#FF5C00]">29€ HT</strong> en offre de lancement (49€ HT tarif public)</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="font-bold text-white mb-1">Stand payant ≤ 164€/jour</div>
            <p className="text-sm">Forfait fixe <strong className="text-white">+49€ HT</strong> en plus du prix du stand</p>
            <p className="text-xs text-gray-500 mt-1">Ex : stand 100€/j → vous payez 149€ HT au total</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="font-bold text-white mb-1">Stand payant ≥ 164,50€/jour</div>
            <p className="text-sm"><strong className="text-white">+30%</strong> du montant du stand HT</p>
            <p className="text-xs text-gray-500 mt-1">Ex : stand 200€/j → frais Festin 60€ HT, total 260€ HT</p>
          </div>
        </div>

        <h2 className="text-xl font-bold text-white">Transparence garantie</h2>
        <p className="text-sm">Le détail prix stand / frais Festin est toujours affiché en deux lignes distinctes avant la validation du paiement. Jamais un total opaque.</p>

        <h2 className="text-xl font-bold text-white">Pour les organisateurs</h2>
        <p className="text-sm">Festin est <strong className="text-white">100% gratuit</strong>. Vous recevez l'intégralité du prix d'emplacement sur votre compte Stripe Connect. Festin ne prend aucune commission sur votre part.</p>
      </div>
    ),
  },

  'guide-electrique-festival': {
    title: "L'installation électrique en festival",
    category: 'Logistique',
    icon: <Zap className="text-[#FF5C00]" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">L'électricité est le nerf de la guerre en festival. Une mauvaise gestion peut entraîner des coupures générales ou endommager votre matériel.</p>

        <h2 className="text-xl font-bold text-white">1. Évaluez vos besoins réels</h2>
        <p className="text-sm">Faites l'inventaire de chaque appareil (frigo, plancha, machine à café, éclairage) et notez la puissance en Watts. Watts = Volts × Ampères. Sur un festival, on exprime souvent les besoins en Ampérage : 16A ≈ 3,5 kW, 32A ≈ 7 kW.</p>
        <p className="text-sm"><strong className="text-white">Sur Festin :</strong> renseignez votre besoin électrique en kW dans votre profil exposant — c'est la première info que regardent les organisateurs pour valider votre candidature.</p>

        <h2 className="text-xl font-bold text-white">2. Le matériel indispensable</h2>
        <ul className="list-disc pl-5 space-y-2 text-sm">
          <li><strong className="text-white">Câbles HO7RN-F</strong> — Norme obligatoire pour l'extérieur. Résistent à l'écrasement et à l'humidité.</li>
          <li><strong className="text-white">Boîtiers de distribution</strong> — Avec disjoncteurs différentiels 30 mA.</li>
          <li><strong className="text-white">Passages de câbles</strong> — Indispensables si des personnes circulent sur vos câbles.</li>
        </ul>

        <h2 className="text-xl font-bold text-white">3. L'erreur à éviter absolument</h2>
        <div className="bg-red-500/10 border-l-4 border-red-500 p-4 rounded-r-lg text-sm">
          <strong className="text-red-400">L'effet bobine :</strong> Ne laissez jamais un câble enroulé sous forte charge. La chaleur s'accumule et le câble peut fondre ou provoquer un incendie. Déroulez toujours intégralement.
        </div>
      </div>
    ),
  },

  'checklist-jour-j': {
    title: "Checklist avant le jour J",
    category: 'Préparation',
    icon: <Calendar className="text-[#00A3FF]" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">Le stress monte ? Voici la checklist pour arriver sur site l'esprit serein.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { title: 'Administratif', items: ['Contrat Festin téléchargé', 'Assurance RC Pro à jour', 'Kbis / SIRET disponible', 'Coordonnées de l\'organisateur'] },
            { title: 'Matériel électrique', items: ['Câbles HO7RN-F déroulés', 'Boîtier distribution + disjoncteurs', 'Multiprises IP44', 'Lampes de secours'] },
            { title: 'Stand & installation', items: ['Dimensions vérifiées vs contrat', 'Ancrage tente / structure', 'Gaffer tape et outillage de base', 'Trousse de premiers secours'] },
            { title: 'Logistique', items: ['Horaires d\'accès véhicule confirmés', 'Vignette / badge exposant', 'Numéro régisseur site', 'Plan de masse imprimé'] },
          ].map(section => (
            <div key={section.title} className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h3 className="font-bold text-white mb-3">{section.title}</h3>
              <ul className="space-y-2">
                {section.items.map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <span className="w-4 h-4 border border-white/20 rounded shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    ),
  },

  'fonctionnement-acomptes': {
    title: 'Comment fonctionnent les paiements ?',
    category: 'Facturation',
    icon: <CreditCard className="text-purple-500" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">Le paiement sur Festin est déclenché uniquement quand l'organisateur retient votre candidature.</p>
        <h2 className="text-xl font-bold text-white">Le flux de paiement</h2>
        <ol className="list-decimal pl-5 space-y-3 text-sm">
          <li><strong className="text-white">Candidature acceptée</strong> → Statut "Paiement requis"</li>
          <li><strong className="text-white">Paiement des frais Festin</strong> via Stripe (29€ HT lancement)</li>
          <li><strong className="text-white">Statut "Confirmé"</strong> + contrat PDF généré automatiquement</li>
        </ol>
      </div>
    ),
  },

  'annulation-intemperies': {
    title: "Conditions d'annulation en cas d'intempéries",
    category: 'Organisation',
    icon: <CloudRain className="text-blue-400" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">Que se passe-t-il si la météo s'en mêle ?</p>
        <h2 className="text-xl font-bold text-white">Force Majeure vs Décision Administrative</h2>
        <p className="text-sm">Il est crucial de distinguer une pluie fine d'un arrêté préfectoral de fermeture pour danger météorologique. Dans le premier cas, l'organisateur reste libre de maintenir l'événement. Dans le second, une annulation officielle ouvre droit à remboursement selon les conditions du contrat.</p>
        <div className="p-5 bg-blue-500/10 rounded-2xl border border-blue-500/20 text-sm italic">
          "En règle générale, une annulation décidée par l'organisateur pour raison de sécurité donne lieu à un remboursement partiel ou total du prix de l'emplacement, hors frais de dossier. Référez-vous aux conditions de votre contrat Festin."
        </div>
      </div>
    ),
  },

  'optimiser-stand-festivaliers': {
    title: 'Optimiser son stand pour attirer plus de festivaliers',
    category: 'Marketing',
    icon: <Sparkles className="text-yellow-400" />,
    content: (
      <div className="space-y-6 text-gray-300">
        <p className="text-lg">En festival, vous avez 3 secondes pour capter l'attention d'un passant.</p>
        <h2 className="text-xl font-bold text-white">La règle des 3 niveaux de lecture</h2>
        <ul className="space-y-3 text-sm">
          <li><strong className="text-white">Niveau 1 (de loin)</strong> — Un drapeau ou une enseigne haute pour être vu au-dessus de la foule. Couleurs vives, logo reconnaissable.</li>
          <li><strong className="text-white">Niveau 2 (à distance)</strong> — Le nom de votre concept et votre spécialité en gros caractères.</li>
          <li><strong className="text-white">Niveau 3 (de près)</strong> — Le détail des prix et les visuels produits.</li>
        </ul>
        <div className="bg-[#FF5C00]/10 border border-[#FF5C00]/20 rounded-2xl p-5 text-sm">
          <strong className="text-[#FF5C00]">Conseil Festin :</strong> Soignez également votre profil en ligne. Les organisateurs regardent vos photos de galerie avant de vous retenir. Des photos de votre stand "en action" valent mieux que des visuels génériques.
        </div>
      </div>
    ),
  },
};

export default function SupportArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = slug ? ARTICLES[slug] : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-[#121212] text-white flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-gray-400">Article introuvable.</p>
        <button
          onClick={() => navigate('/support')}
          className="flex items-center gap-2 text-[#FF5C00] hover:underline text-sm font-medium"
        >
          <ChevronLeft className="w-4 h-4" />
          Retour au centre d'aide
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] text-white font-sans">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate('/support')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm mb-10"
        >
          <ChevronLeft className="w-4 h-4" />
          Centre d'aide
        </button>

        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold tracking-widest uppercase text-gray-500">{article.category}</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-black mb-10 leading-tight">{article.title}</h1>

        <div className="prose prose-invert max-w-none text-gray-300 leading-relaxed">
          {article.content}
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex items-center justify-between">
          <button
            onClick={() => navigate('/support')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Retour au centre d'aide
          </button>
          <button
            onClick={() => navigate('/contact')}
            className="text-sm text-[#FF5C00] hover:underline"
          >
            Une question ? Contactez-nous
          </button>
        </div>
      </div>
    </div>
  );
}
